/** UI/API comment types for tasks (distinct from task Status). */
export type TaskCommentInputType =
  | "Routine"
  | "Apply Reschedule"
  | "Apply Completion"
  | "Reschedule";

/** Display types in comment tables and notice board. */
export type TaskCommentDisplayType =
  | TaskCommentInputType
  | "Complete"
  | "Notice"
  | "Reply"
  | "Progress Update";

/**
 * Map DB/API comment_type (+ optional content) to UI label.
 * - Apply Completion: participant requests completion
 * - Complete: manager marks task/checkpoint completed
 */
export function normalizeTaskCommentDisplayType(
  type: string,
  content?: string
): TaskCommentDisplayType {
  const normalized = String(type ?? "routine").toLowerCase().trim();
  const body = String(content ?? "").trim().toLowerCase();

  if (body.includes("[reply to #")) return "Reply";
  if (normalized === "notice") return "Notice";
  if (normalized === "routine") return "Routine";
  // Edit-task auto comments use content prefix "Reschedule changes:"
  // (legacy rows were stored as apply reschedule).
  if (body.startsWith("reschedule changes:")) return "Reschedule";
  if (normalized === "reschedule") return "Reschedule";
  if (normalized === "apply reschedule" || normalized === "applyreschedule") {
    return "Apply Reschedule";
  }
  if (
    normalized === "completed" ||
    normalized === "complete" ||
    normalized === "completion"
  ) {
    return "Complete";
  }

  const isApplyCompletionKind =
    normalized === "apply completion" ||
    normalized === "applycompletion" ||
    normalized === "apply completed" ||
    normalized === "applycompleted" ||
    normalized === "apply complete";

  if (isApplyCompletionKind) {
    // Legacy rows: manager Complete used Apply Completion + fixed body text
    if (body === "task completed." || body === "task completed") return "Complete";
    if (body.startsWith("checkpoint completed:")) return "Complete";
    return "Apply Completion";
  }

  if (normalized === "reply") return "Reply";

  const fallback = String(type ?? "Routine").trim();
  if (!fallback) return "Routine";
  return (fallback.charAt(0).toUpperCase() + fallback.slice(1).toLowerCase()) as TaskCommentDisplayType;
}

/** Payload commentType for TaskComment/AddComment. */
export function taskCommentTypeForApi(type: string): string {
  const normalized = String(type ?? "routine").toLowerCase().trim();
  if (normalized === "apply completion" || normalized === "apply complete") {
    return "Apply Completion";
  }
  if (normalized === "complete") return "Complete";
  if (normalized === "apply reschedule") return "Apply Reschedule";
  if (normalized === "reschedule") return "reschedule";
  if (normalized === "notice") return "notice";
  return type || "Routine";
}
